import apiClient from "@services/api/apiClient.jsx";

/**
 * 카메라 앵글 관련 API 서비스
 */
const cameraAngleApi = {
    /**
     * 카메라 앵글 목록 조회 (페이징)
     * @param {Object} params 검색 파라미터
     * @returns {Promise<Object>} 카메라 앵글 목록
     */
    getCameraAngles: async (params = {}) => {
        try {
            const response = await apiClient.get('/camera-angles', { params });
            return response.data;
        } catch (error) {
            console.error('카메라 앵글 목록 조회 실패:', error);
            throw error;
        }
    },

    /**
     * 모든 카메라 앵글 조회
     * @returns {Promise<Array>} 모든 카메라 앵글 목록
     */
    getAllCameraAngles: async () => {
        try {
            const response = await apiClient.get('/camera-angles/all');
            return response.data;
        } catch (error) {
            console.error('모든 카메라 앵글 조회 실패:', error);
            throw error;
        }
    },

    /**
     * 카메라 앵글 상세 조회
     * @param {number} id 카메라 앵글 ID
     * @returns {Promise<Object>} 카메라 앵글 상세 정보
     */
    getCameraAngle: async (id) => {
        try {
            const response = await apiClient.get(`/camera-angles/${id}`);
            return response.data;
        } catch (error) {
            console.error(`카메라 앵글 ID ${id} 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 카메라 앵글 생성
     * @param {FormData} formData 카메라 앵글 생성 데이터
     * @returns {Promise<Object>} 생성된 카메라 앵글 정보
     */
    createCameraAngle: async (formData) => {
        try {
            const response = await apiClient.post('/camera-angles', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            console.error('카메라 앵글 생성 실패:', error);
            throw error;
        }
    },

    /**
     * 카메라 앵글 수정
     * @param {number} id 카메라 앵글 ID
     * @param {FormData} formData 카메라 앵글 수정 데이터
     * @returns {Promise<Object>} 수정된 카메라 앵글 정보
     */
    updateCameraAngle: async (id, formData) => {
        try {
            const response = await apiClient.put(`/camera-angles/${id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            console.error(`카메라 앵글 ID ${id} 수정 실패:`, error);
            throw error;
        }
    },

    /**
     * 카메라 앵글 삭제
     * @param {number} id 카메라 앵글 ID
     * @returns {Promise<Object>} 삭제 결과
     */
    deleteCameraAngle: async (id) => {
        try {
            const response = await apiClient.delete(`/camera-angles/${id}`);
            return response.data;
        } catch (error) {
            console.error(`카메라 앵글 ID ${id} 삭제 실패:`, error);
            throw error;
        }
    },

    /**
     * 카메라 앵글 일괄 삭제
     * @param {Array<number>} ids 삭제할 카메라 앵글 ID 배열
     * @returns {Promise<Object>} 삭제 결과
     */
    deleteCameraAngles: async (ids) => {
        try {
            const response = await apiClient.delete('/camera-angles/bulk', {
                data: ids,
            });
            return response.data;
        } catch (error) {
            console.error('카메라 앵글 일괄 삭제 실패:', error);
            throw error;
        }
    },

    /**
     * 앵글명 중복 체크
     * @param {string} angleName 앵글명
     * @param {number} excludeId 제외할 ID (수정 시)
     * @returns {Promise<Object>} 중복 체크 결과
     */
    checkAngleNameDuplicate: async (angleName, excludeId = null) => {
        try {
            const params = { angleName };
            if (excludeId) {
                params.excludeId = excludeId;
            }
            
            const response = await apiClient.get('/camera-angles/check-duplicate', { params });
            return response.data;
        } catch (error) {
            console.error('앵글명 중복 체크 실패:', error);
            throw error;
        }
    },

    /**
     * 배포 상태 토글
     * @param {number} id 카메라 앵글 ID
     * @param {string} updatedBy 수정자
     * @returns {Promise<Object>} 수정된 카메라 앵글 정보
     */
    toggleDeployStatus: async (id, updatedBy) => {
        try {
            const response = await apiClient.patch(`/camera-angles/${id}/toggle-deploy`, null, {
                params: { updatedBy },
            });
            return response.data;
        } catch (error) {
            console.error(`카메라 앵글 ID ${id} 배포 상태 변경 실패:`, error);
            throw error;
        }
    },

    /**
     * 지점별 카메라 앵글 조회
     * @param {string} branchId 지점 ID
     * @returns {Promise<Array>} 지점별 카메라 앵글 목록
     */
    getCameraAnglesByBranch: async (branchId) => {
        try {
            const response = await apiClient.get(`/camera-angles/by-branch/${branchId}`);
            return response.data;
        } catch (error) {
            console.error(`지점 ID ${branchId}의 카메라 앵글 조회 실패:`, error);
            throw error;
        }
    },

    /**
     * 활성 카메라 앵글 조회
     * @returns {Promise<Array>} 활성 카메라 앵글 목록
     */
    getActiveCameraAngles: async () => {
        try {
            const response = await apiClient.get('/camera-angles/active');
            return response.data;
        } catch (error) {
            console.error('활성 카메라 앵글 조회 실패:', error);
            throw error;
        }
    },

    /**
     * FormData 생성 헬퍼 함수
     * @param {Object} data 변환할 데이터 객체
     * @returns {FormData} 생성된 FormData 객체
     */
    createFormData: (data) => {
        const formData = new FormData();
        
        // 디버깅용 로그
        console.log('FormData 생성 중, 원본 데이터:', data);
        
        // 기본 필드들 추가
        Object.keys(data).forEach(key => {
            if (key === 'branches' && Array.isArray(data[key])) {
                // 지점 정보는 JSON 문자열로 변환
                formData.append('branches', JSON.stringify(data[key]));
            } else if (key === 'angleDataFile' && data[key]) {
                // 파일은 직접 추가
                formData.append('angleDataFile', data[key]);
            } else if (data[key] !== null && data[key] !== undefined) {
                // 다른 필드들은 문자열로 변환하여 추가
                formData.append(key, String(data[key]));
            }
        });
        
        // FormData 내용 확인 (디버깅용)
        console.log('생성된 FormData 내용:');
        for (let [key, value] of formData.entries()) {
            console.log(`${key}: ${value}`);
        }
        
        return formData;
    }
};

export default cameraAngleApi;
